import React, { useEffect, useState } from 'react';
import PlatformAvatar from '../components/PlatformAvatar';
import PlatformBadge from '../components/PlatformBadge';
import PlatformButton from '../components/PlatformButton';
import PlatformField from '../components/PlatformField';
import PlatformSectionTitle from '../components/PlatformSectionTitle';
import PlatformToggle from '../components/PlatformToggle';
import TeamSection from '../components/TeamSection';
import ProductsSection from '../components/ProductsSection';
import BillingSection from '../components/BillingSection';
import { ADMIN_SETTINGS_TABS, isAdmin } from '../lib/permissions';

const ALL_TABS = [
  ['profile', 'Profile'],
  ['team', 'Team'],
  ['products', 'Products'],
  ['automation', 'Automation'],
  ['billing', 'Billing'],
  ['security', 'Security'],
];

function SettingsView({
  user = {},
  profile = {},
  company = null,
  members = [],
  pendingInvites = [],
  onSaveProfile,
  onUpdatePassword,
  onInviteMember,
  onUpdateMemberRole,
  onRemoveMember,
  onRevokeInvite,
  onToggleProduct,
  saving = false,
}) {
  const admin = isAdmin(user);
  const visibleTabs = ALL_TABS.filter(
    ([id]) => admin || !ADMIN_SETTINGS_TABS.includes(id)
  );

  const [tab, setTab] = useState('profile');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [sendCards, setSendCards] = useState(true);
  const [pauseWeekends, setPauseWeekends] = useState(false);

  useEffect(() => {
    setFullName(user.displayName || user.name || profile.name || '');
    setRole(profile.jobTitle || user.jobTitle || '');
    setEmail(user.email || profile.email || '');
    setCompanyName(profile.companyName || user.companyName || user.company || '');
  }, [user, profile]);

  useEffect(() => {
    if (!admin && ADMIN_SETTINGS_TABS.includes(tab)) {
      setTab('profile');
    }
  }, [admin, tab]);

  const displayName = fullName || 'User';

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    onUpdatePassword?.(password, confirmPassword);
  };

  return (
    <div className="settings-view platform-scroll platform-fade">
      <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>Settings</h2>
      <p style={{ fontSize: 13.5, color: 'var(--muted)', margin: '0 0 22px' }}>
        Manage your profile, team, products, automation, billing, and security.
      </p>

      <div className="platform-row settings-tabs">
        {visibleTabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`settings-tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <div className="platform-card settings-card">
          <div className="platform-row settings-profile-head">
            <PlatformAvatar name={displayName} size={72} photoUrl={user.photoUrl || profile.photoUrl} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>{displayName}</div>
              <div style={{ fontSize: 13.5, color: 'var(--muted)', marginBottom: 10 }}>
                {role}
                {companyName ? ` · ${companyName}` : ''}
              </div>
              <PlatformButton variant="outline" size="sm">
                Change photo
              </PlatformButton>
            </div>
          </div>
          <div className="settings-grid">
            <PlatformField label="Full name">
              <input
                className="platform-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </PlatformField>
            <PlatformField label="Role">
              <input className="platform-input" value={role} onChange={(e) => setRole(e.target.value)} />
            </PlatformField>
            <PlatformField label="Email">
              <input
                className="platform-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </PlatformField>
            <PlatformField label="Company">
              <input
                className="platform-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </PlatformField>
          </div>
          <div className="platform-row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <PlatformButton
              variant="primary"
              size="md"
              disabled={saving}
              onClick={() => onSaveProfile?.({ fullName, role, email, company: companyName })}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </PlatformButton>
          </div>
        </div>
      )}

      {tab === 'team' && admin && (
        <TeamSection
          members={members}
          pendingInvites={pendingInvites}
          companyID={company?.companyID || user.companyID || user.companyId || ''}
          onInvite={onInviteMember}
          onUpdateRole={onUpdateMemberRole}
          onRemove={onRemoveMember}
          onRevokeInvite={onRevokeInvite}
        />
      )}

      {tab === 'products' && admin && (
        <ProductsSection
          enabledProducts={company?.enabledProducts || []}
          onToggle={onToggleProduct}
        />
      )}

      {tab === 'automation' && (
        <div className="platform-card settings-card">
          <PlatformSectionTitle icon="sparkle">Default cadence & triggers</PlatformSectionTitle>
          <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 18px' }}>
            Applied to every new contact unless overridden.
          </p>
          <div className="settings-row platform-row">
            <span style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>Default touch frequency</span>
            <PlatformBadge tone="neutral">{profile.frequency || '1 Month'}</PlatformBadge>
          </div>
          <div className="settings-row platform-row">
            <span style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>
              Auto-approve high-confidence drafts
            </span>
            <PlatformToggle on={autoApprove} onChange={setAutoApprove} ariaLabel="Auto-approve drafts" />
          </div>
          <div className="settings-row platform-row">
            <span style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>
              Send birthday & holiday cards
            </span>
            <PlatformToggle on={sendCards} onChange={setSendCards} ariaLabel="Send cards" />
          </div>
          <div className="settings-row platform-row">
            <span style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500 }}>Pause sends on weekends</span>
            <PlatformToggle on={pauseWeekends} onChange={setPauseWeekends} ariaLabel="Pause weekends" />
          </div>
        </div>
      )}

      {tab === 'billing' && admin && <BillingSection company={company} />}

      {tab === 'security' && (
        <form onSubmit={handlePasswordSubmit} className="platform-card settings-card" style={{ maxWidth: 520 }}>
          <PlatformSectionTitle icon="settings">Reset password</PlatformSectionTitle>
          <PlatformField label="New password">
            <input
              className="platform-input"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </PlatformField>
          <PlatformField label="Confirm password">
            <input
              className="platform-input"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </PlatformField>
          <PlatformButton variant="primary" size="md" type="submit" style={{ marginTop: 6 }}>
            Update password
          </PlatformButton>
        </form>
      )}
    </div>
  );
}

export default SettingsView;
